import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  PdfTemplate,
  PdfTemplatePage,
  PdfTemplateElement,
  PdfTemplateElementType,
  PdfTemplateLabel,
  PdfTemplateImage,
  PdfTemplateTableValue,
  PdfTemplateLine,
  PdfTemplateShape,
} from '../entities/form-pdf-template.entity';
import { Form, FormField } from '../entities/form.entity';
import { Tag } from '../entities/app.entity';
import {
  expandPdfPagesForBoundLists,
  extractBoundListItems,
  getBoundListSliceMeta,
} from '../helpers/pdf-list-pagination.helper';
import {
  applyBoundTableSlice,
  expandPdfPagesForBoundTables,
  extractBoundTableData,
  getBoundTableSliceMeta,
} from '../helpers/pdf-table-pagination.helper';
import { AdminArtifactsSearchService } from './admin-artifacts-search.service';
import { AdminFormsService } from './admin-forms.service';
import { PdfService } from './pdf.service';
export const PDF_DEBUG_SHOW_EMPTY_TAG_PLACEHOLDERS = false;

@Injectable({
  providedIn: 'root',
})
export class FormPdfTemplateGeneratorService {
  constructor(
    private artifactsSearchService: AdminArtifactsSearchService,
    private adminFormsService: AdminFormsService,
    private pdfService: PdfService,
    private sanitizer: DomSanitizer
  ) { }

  private shouldShowEmptyTagPlaceholder(): boolean {
    return PDF_DEBUG_SHOW_EMPTY_TAG_PLACEHOLDERS;
  }

  private resolveEmptyTagText(
    tagName: string,
    showPlaceholder: boolean,
    suffix?: string
  ): string {
    if (!showPlaceholder) {
      return '';
    }
    return suffix ? `[${tagName} - ${suffix}]` : `[${tagName}]`;
  }

  private isEmptyBoundValue(value: unknown): boolean {
    if (value === undefined || value === null) {
      return true;
    }
    if (typeof value === 'string') {
      return value.trim() === '';
    }
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    if (typeof value === 'object' && value !== null && 'value' in value) {
      return this.isEmptyBoundValue((value as { value: unknown; }).value);
    }
    return false;
  }

  private extractBoundValue(tagDataItem: unknown): string | null {
    if (this.isEmptyBoundValue(tagDataItem)) {
      return null;
    }
    if (typeof tagDataItem === 'string' || typeof tagDataItem === 'number') {
      return String(tagDataItem);
    }
    if (Array.isArray(tagDataItem)) {
      return null;
    }
    if (
      typeof tagDataItem === 'object' &&
      tagDataItem !== null &&
      'value' in tagDataItem
    ) {
      const inner = (tagDataItem as { value: unknown; }).value;
      if (this.isEmptyBoundValue(inner)) {
        return null;
      }
      return String(inner);
    }
    return String(tagDataItem);
  }

  generatePreviewTagData(
    template: PdfTemplate,
    form: Form
  ): { [tag: string]: any; } {
    const tagData: { [tag: string]: any; } = {};
    const collectedTags = new Set<string>();

    // Collect all tags from template elements
    template.pages.forEach((page) => {
      [page.header, page.body, page.footer].forEach((section) => {
        section.elements.forEach((element) => {
          const tagValue =
            'bindingTagValue' in element
              ? (element as any).bindingTagValue
              : null;
          const tagUuid =
            'bindingTagUuid' in element
              ? (element as any).bindingTagUuid
              : null;

          if (tagValue) {
            collectedTags.add(tagValue.toUpperCase());
          } else if (tagUuid) {
            collectedTags.add(tagUuid.toUpperCase());
          }
        });
      });
    });

    // Create mock data for each collected tag
    collectedTags.forEach((tag) => {
      const upperCaseTag = tag.toUpperCase();

      // Find field associated with this tag
      const field = form.fields?.find((f: FormField) => {
        return f.metadata?.tags?.some(
          (t: Tag) => t.value?.toUpperCase() === upperCaseTag
        );
      });

      if (!PDF_DEBUG_SHOW_EMPTY_TAG_PLACEHOLDERS) {
        return;
      }

      if (field) {
        switch (field.type) {
          case 'table': {
            // For tables in preview, don't generate mock data
            // This allows generateLabelHtml to generate structure from form definition
            // tagData[upperCaseTag] is intentionally left undefined
            break;
          }
          case 'file-upload': {
            tagData[upperCaseTag] = {
              field_id: field.fieldId,
              value: `[${upperCaseTag}]`,
              url: '',
            };
            break;
          }
          default: {
            tagData[upperCaseTag] = {
              field_id: field.fieldId,
              value: `[${upperCaseTag}]`,
            };
          }
        }
      } else {
        tagData[upperCaseTag] = {
          value: `[${upperCaseTag}]`,
        };
      }
    });

    return tagData;
  }

  async buildTagData(formData: any): Promise<{ [tag: string]: any; }> {
    const tagData: { [tag: string]: any; } = {};
    const mapping = await this.artifactsSearchService
      .getFieldMapping()
      .catch(() => []);
    const fileKeys: string[] = [];
    const keyToTagMap: { [key: string]: string; } = {};

    mapping.forEach((obj: any) => {
      obj.tags.forEach((tag: string) => {
        const upperCaseTag = tag.toUpperCase();
        if (!tagData[upperCaseTag]) {
          const key = obj.field_id.replace(/-/g, '');
          if (formData[key]) {
            tagData[upperCaseTag] = { ...obj, ...formData[key] };

            if (tagData[upperCaseTag].value?.keyFile) {
              fileKeys.push(tagData[upperCaseTag].value.keyFile);
              keyToTagMap[upperCaseTag] = tagData[upperCaseTag].value.keyFile;
            }
          } else {
            const matchingKey = Object.keys(formData).find((dataKey) => {
              const cleanedDataKey = dataKey.replace(/-/g, '');
              return cleanedDataKey.endsWith(key);
            });

            if (matchingKey && formData[matchingKey]) {
              tagData[upperCaseTag] = { ...obj, ...formData[matchingKey] };

              if (tagData[upperCaseTag].value?.keyFile) {
                fileKeys.push(tagData[upperCaseTag].value.keyFile);
                keyToTagMap[upperCaseTag] = tagData[upperCaseTag].value.keyFile;
              }
            }
          }
        }
      });
    });

    if (fileKeys.length > 0) {
      await this.updateWithSignedLinks(fileKeys, keyToTagMap, tagData);
    }

    const sortedTagData: { [key: string]: any; } = {};
    Object.keys(tagData)
      .sort()
      .forEach((key) => {
        sortedTagData[key] = tagData[key];
      });

    return sortedTagData;
  }

  private async updateWithSignedLinks(
    fileKeys: string[],
    keyToTagMap: { [key: string]: string; },
    tagData: { [tag: string]: any; }
  ): Promise<void> {
    const results = (await this.artifactsSearchService.getArtifactsSignedLinks(
      fileKeys
    )) as any;

    const fetchPromises = results.map(async (result: any) => {
      const tag = Object.keys(keyToTagMap).find(
        (key) => keyToTagMap[key] === result.key
      );
      if (tag) {
        const upperCaseTag = tag.toUpperCase();
        if (tagData[upperCaseTag]) {
          const fileUrl = result.url;

          try {
            const response = await fetch(fileUrl);
            const blob = await response.blob();

            if (blob.type === 'application/pdf') {
              // Handle PDF files if needed
              const arrayBuffer = await blob.arrayBuffer();
              tagData[upperCaseTag].pdfBytes = new Uint8Array(arrayBuffer);
            } else {
              const dataUrl = await this.blobToDataURL(blob);
              tagData[upperCaseTag].url = dataUrl;
            }
          } catch (error) {
            console.error('Error getting files:', error);
          }
        }
      }
    });

    await Promise.all(fetchPromises);
  }

  /**
   * Convert blob to data URL
   */
  private blobToDataURL(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = function () {
        resolve(reader.result as string);
      };
      reader.onerror = function () {
        reject(new Error('Error reading Blob Data'));
      };
      reader.readAsDataURL(blob);
    });
  }

  private convertSimpleDictionaryToTagData(dictionary: {
    [tag: string]: any;
  }): { [tag: string]: any; } {
    const tagData: { [tag: string]: any; } = {};
    Object.keys(dictionary).forEach((tag) => {
      const upperCaseTag = tag.toUpperCase();
      const value = dictionary[tag];

      if (value !== null && typeof value === 'object') {
        tagData[upperCaseTag] = value;
      } else {
        tagData[upperCaseTag] = { value: value };
      }
    });
    return tagData;
  }

  getTableData(
    tag: string,
    tagData: { [tag: string]: any; },
    form: Form
  ): { definition: any; data: any; } {
    const tagDataItem = tagData[tag.toUpperCase()];
    const tableKey = tagDataItem?.field_id;
    const definition = form.fields.find(
      (f: FormField) => f.fieldId === tableKey
    )?.table?.fields;
    return { definition, data: tagDataItem };
  }

  async generatePreviewHtml(
    template: PdfTemplate,
    tagData: { [tag: string]: any; },
    form: Form,
    assets: Record<string, string | any> = {},
    isDraft: boolean = false,
    watermarkText: string = 'DRAFT'
  ): Promise<string> {
    const expandedPages = expandPdfPagesForBoundLists(
      expandPdfPagesForBoundTables(template, tagData, form),
      template,
      tagData,
      form
    );
    const pagesHtml = expandedPages
      .map((page) =>
        this.generatePreviewPageHtml(
          page,
          tagData,
          form,
          assets,
          template,
          isDraft,
          watermarkText
        )
      )
      .join('\n');

    return `<div class="pdf-content">
      ${pagesHtml}
    </div>`;
  }

  async generateHtml(
    template: PdfTemplate,
    tagData: { [tag: string]: any; },
    form: Form,
    assets: Record<string, string | any> = {}
  ): Promise<string> {
    const expandedPages = expandPdfPagesForBoundLists(
      expandPdfPagesForBoundTables(template, tagData, form),
      template,
      tagData,
      form
    );
    const pagesHtml = expandedPages
      .map((page) =>
        this.generatePageHtml(page, tagData, form, assets, template)
      )
      .join('\n');

    return `<div class="pdf-content preview-container flex flex-col space-y-8 print:space-y-0">
      ${pagesHtml}
    </div>`;
  }

  private generatePreviewPageHtml(
    page: PdfTemplatePage,
    tagData: { [tag: string]: any; },
    form: Form,
    assets: Record<string, string | any>,
    template: PdfTemplate,
    isDraft: boolean = false,
    watermarkText: string = 'DRAFT'
  ): string {
    const orientation = template.orientation;
    const pageWidth = orientation === 'portrait' ? '210mm' : '297mm';
    const pageHeight = orientation === 'portrait' ? '297mm' : '210mm';
    const showPlaceholder = this.shouldShowEmptyTagPlaceholder();

    const watermarkHtml = isDraft
      ? `<div class="watermark" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-30deg); font-size: 256px; color: rgba(100, 100, 100, 0.3); font-weight: bold; text-align: center; white-space: nowrap; pointer-events: none; z-index: 1000; width: 100%; height: fit-content;">${watermarkText}</div>`
      : '';

    const headerHtml = this.generatePreviewSectionHtml(
      page.header,
      'header',
      tagData,
      form,
      assets,
      showPlaceholder,
      page
    );
    const bodyHtml = this.generatePreviewSectionHtml(
      page.body,
      'body',
      tagData,
      form,
      assets,
      showPlaceholder,
      page
    );
    const footerHtml = this.generatePreviewSectionHtml(
      page.footer,
      'footer',
      tagData,
      form,
      assets,
      showPlaceholder,
      page
    );

    return `<div class="pdf-page page-canvas" style="width: ${pageWidth}; height: ${pageHeight}; background: white; position: relative; display: flex; flex-direction: column; page-break-after: always;">
      ${watermarkHtml}
      ${headerHtml}
      ${bodyHtml}
      ${footerHtml}
    </div>`;
  }

  private generatePreviewSectionHtml(
    section: any,
    sectionName: 'header' | 'body' | 'footer',
    tagData: { [tag: string]: any; },
    form: Form,
    assets: Record<string, string | any>,
    showPlaceholder: boolean,
    page?: PdfTemplatePage
  ): string {
    const elementsHtml = section.elements
      .map((element: PdfTemplateElement) =>
        this.generatePreviewElementHtml(
          element,
          tagData,
          form,
          assets,
          showPlaceholder
        )
      )
      .join('\n');

    const backgroundColor = section.backgroundColor || '#ffffff';
    const sectionClass = `section-${sectionName}`;

    let heightStyle = '';
    let flexStyle = '';

    if (sectionName === 'body') {
      // Body section should flex to fill remaining space
      flexStyle = 'flex: 1 1 auto;';
      if (page) {
        const headerHeight = page.header?.height || 30;
        const footerHeight = page.footer?.height || 20;
        const orientation = (page as any).orientation || 'portrait';
        const pageHeight = orientation === 'portrait' ? 297 : 210;
        heightStyle = `height: calc(${pageHeight}mm - ${headerHeight}mm - ${footerHeight}mm);`;
      }
    } else {
      // Header and footer have fixed heights
      const height = section.height
        ? `${section.height}mm`
        : sectionName === 'header'
          ? '30mm'
          : '20mm';
      heightStyle = `height: ${height};`;
      flexStyle = 'flex-shrink: 0;';
    }

    return `<div class="${sectionClass}" style="${heightStyle} ${flexStyle} background-color: ${backgroundColor}; position: relative; overflow: ${sectionName === 'body' ? 'hidden' : 'visible'};">
      ${elementsHtml}
    </div>`;
  }

  private generatePreviewElementHtml(
    element: PdfTemplateElement,
    tagData: { [tag: string]: any; },
    form: Form,
    assets: Record<string, string | any>,
    showPlaceholder: boolean
  ): string {
    switch (element.type) {
      case PdfTemplateElementType.LABEL: {
        return this.generateLabelHtml(
          element as PdfTemplateLabel,
          tagData,
          form,
          showPlaceholder
        );
      }
      case PdfTemplateElementType.IMAGE: {
        return this.generateImageHtml(
          element as PdfTemplateImage,
          tagData,
          assets,
          showPlaceholder
        );
      }
      case PdfTemplateElementType.TABLE_VALUE: {
        return this.generatePreviewTableHtml(
          element as PdfTemplateTableValue,
          tagData,
          form
        );
      }
      case PdfTemplateElementType.LINE: {
        return this.generateLineHtml(element as PdfTemplateLine);
      }
      case PdfTemplateElementType.SHAPE: {
        return this.generateShapeHtml(element as PdfTemplateShape);
      }
      default:
        return '';
    }
  }

  private generatePreviewTableHtml(
    tableValue: PdfTemplateTableValue,
    tagData: { [tag: string]: any; },
    form: Form
  ): string {
    const tableData = this.getTableData(
      tableValue.bindingTagValue || tableValue.bindingTagUuid,
      tagData,
      form
    );

    if (!tableData.definition || !tableData.data) {
      return tableValue.emptyPlaceholder
        ? `<div style="position: absolute; left: ${tableValue.x}mm; top: ${tableValue.y}mm;">${this.escapeHtml(
          tableValue.emptyPlaceholder
        )}</div>`
        : '';
    }

    const columns = tableData.definition || [];
    const rows = tableData.data?.value?.rows || [];

    let tableHtml = `<table style="width: 100%; border-collapse: collapse; font-size: 10pt;">`;

    tableHtml += '<thead><tr>';
    columns.forEach((col: any) => {
      const columnName = col.label || col.name || '';
      tableHtml += `<th style="border: 1px solid #ddd; padding: 8px; background-color: #f3f4f6; text-align: left; font-weight: 600;">${this.escapeHtml(
        columnName
      )}</th>`;
    });
    tableHtml += '</tr></thead>';

    tableHtml += '<tbody>';
    rows.forEach((row: any[]) => {
      tableHtml += '<tr>';
      row.forEach((cell: any) => {
        const cellValue =
          typeof cell === 'object' ? JSON.stringify(cell) : cell;
        tableHtml += `<th style="border: 1px solid #ddd; padding: 8px;">${this.escapeHtml(
          String(cellValue || '')
        )}</th>`;
      });
      tableHtml += '</tr>';
    });
    tableHtml += '</tbody>';
    tableHtml += '</table>';

    const containerStyle = `
      position: absolute;
      left: ${tableValue.x}mm;
      top: ${tableValue.y}mm;
      ${tableValue.width ? `width: ${tableValue.width}mm;` : 'width: auto;'}
      z-index: ${tableValue.zIndex || 1};
    `;

    return `<div class="element-container" style="${containerStyle.trim()}">
      <div class="element-table-value">
        ${tableHtml}
      </div>
    </div>`;
  }

  private generatePageHtml(
    page: PdfTemplatePage,
    tagData: { [tag: string]: any; },
    form: Form,
    assets: Record<string, string | any>,
    template: PdfTemplate
  ): string {
    const orientation = template.orientation;
    const pageWidth = orientation === 'portrait' ? '210mm' : '297mm';
    const pageHeight = orientation === 'portrait' ? '297mm' : '210mm';
    const showPlaceholder = this.shouldShowEmptyTagPlaceholder();

    const headerHtml = this.generateSectionHtml(
      page.header,
      'header',
      tagData,
      form,
      assets,
      showPlaceholder
    );
    const bodyHtml = this.generateSectionHtml(
      page.body,
      'body',
      tagData,
      form,
      assets,
      showPlaceholder
    );
    const footerHtml = this.generateSectionHtml(
      page.footer,
      'footer',
      tagData,
      form,
      assets,
      showPlaceholder
    );

    return `<div class="pdf-page flex flex-col mx-auto bg-white" style="width: ${pageWidth}; height: ${pageHeight}; page-break-after: always;">
      ${headerHtml}
      <div class="flex-grow overflow-hidden" style="height: calc(100% - ${page.header.height || 0}mm - ${page.footer.height || 0}mm);">
        ${bodyHtml}
      </div>
      ${footerHtml}
    </div>`;
  }

  private generateSectionHtml(
    section: any,
    sectionName: 'header' | 'body' | 'footer',
    tagData: { [tag: string]: any; },
    form: Form,
    assets: Record<string, string | any>,
    showPlaceholder: boolean
  ): string {
    const elementsHtml = section.elements
      .map((element: PdfTemplateElement) =>
        this.generateElementHtml(
          element,
          tagData,
          form,
          assets,
          showPlaceholder
        )
      )
      .join('\n');

    const sectionStyle = section.height
      ? `style="height: ${section.height}mm; flex-shrink: 0;"`
      : 'class="flex-grow overflow-hidden"';

    return `<div ${sectionStyle === 'class' ? sectionStyle : sectionStyle} class="relative">
      ${elementsHtml}
    </div>`;
  }

  private generateElementHtml(
    element: PdfTemplateElement,
    tagData: { [tag: string]: any; },
    form: Form,
    assets: Record<string, string | any>,
    showPlaceholder: boolean
  ): string {
    switch (element.type) {
      case PdfTemplateElementType.LABEL:
        return this.generateLabelHtml(
          element as PdfTemplateLabel,
          tagData,
          form,
          showPlaceholder
        );
      case PdfTemplateElementType.IMAGE:
        return this.generateImageHtml(
          element as PdfTemplateImage,
          tagData,
          assets,
          showPlaceholder
        );
      case PdfTemplateElementType.TABLE_VALUE:
        return this.generateTableValueHtml(
          element as PdfTemplateTableValue,
          tagData,
          form
        );
      case PdfTemplateElementType.LINE:
        return this.generateLineHtml(element as PdfTemplateLine);
      case PdfTemplateElementType.SHAPE:
        return this.generateShapeHtml(element as PdfTemplateShape);
      default:
        return '';
    }
  }

  private generateLabelHtml(
    label: PdfTemplateLabel,
    tagData: { [tag: string]: any; },
    form: Form,
    showPlaceholder = false
  ): string {
    let text = label.text;
    let fieldType = (label as any).fieldType;
    let isTableHtml = false;
    let isListHtml = false;
    const listSliceMeta = getBoundListSliceMeta(label);

    const isDataBindingLabel =
      (label as any).isStatic === false ||
      (label as any).needsBinding === true ||
      label.bindingTagUuid;

    if (isDataBindingLabel && label.bindingTagValue) {
      const tagKey = label.bindingTagValue.toUpperCase();
      const tagDataItem = tagData[tagKey];
      const tagName = label.bindingTagValue;

      const resolvedField = form?.fields?.find((f) => {
        if (!f.metadata?.tags) return false;
        return f.metadata.tags.some(
          (tag) => tag.value.toUpperCase() === tagKey
        );
      });

      if (!fieldType && resolvedField) {
        fieldType = resolvedField.type;
      }

      const isTableField =
        fieldType === 'table' || resolvedField?.type === 'table';

      if (Array.isArray(listSliceMeta.listSliceItems)) {
        fieldType = fieldType || 'multiple-choose';
        text = this.renderMultipleChooseList(
          listSliceMeta.listSliceItems,
          tagName,
          showPlaceholder
        );
        isListHtml = true;
      } else if (tagDataItem !== undefined && tagDataItem !== null) {
        if (fieldType === 'multiple-choose') {
          text = this.renderMultipleChooseList(
            extractBoundListItems(tagDataItem),
            tagName,
            showPlaceholder
          );
          isListHtml = true;
        } else if (isTableField) {
          const tableFontSize = label.fontSize || 12;
          if (this.isEmptyBoundValue(tagDataItem)) {
            text = this.renderFormTableStructure(
              resolvedField,
              tagName,
              showPlaceholder,
              tableFontSize
            );
          } else {
            text = this.generateTableFromData(
              tagDataItem,
              tableFontSize,
              label,
              resolvedField
            );
          }
          isTableHtml = true;
        } else if (fieldType === 'file-upload') {
          if (this.isEmptyBoundValue(tagDataItem)) {
            text = this.resolveEmptyTagText(tagName, showPlaceholder, 'File');
          } else {
            text = this.handleFileUpload(tagDataItem);
          }
        } else {
          const tagValue = this.extractBoundValue(tagDataItem);
          text = tagValue ?? this.resolveEmptyTagText(tagName, showPlaceholder);
        }
      } else if (isTableField && form?.fields) {
        text = this.renderFormTableStructure(
          resolvedField,
          tagName,
          showPlaceholder,
          label.fontSize || 12
        );
        isTableHtml = true;
      } else {
        text = this.resolveEmptyTagText(tagName, showPlaceholder);
      }
    }

    const tableSliceMeta = getBoundTableSliceMeta(label);
    const isSlicedTable =
      tableSliceMeta.tableIsContinuation || !!tableSliceMeta.tableSliceRows;
    const isSlicedList =
      listSliceMeta.listIsContinuation ||
      Array.isArray(listSliceMeta.listSliceItems);
    const labelHeight =
      isSlicedTable || isSlicedList || !label.height
        ? ''
        : `height: ${label.height}mm;`;

    const containerStyle = `
      position: absolute;
      left: ${label.x}mm;
      top: ${label.y}mm;
      ${label.width ? `width: ${label.width}mm;` : ''}
      ${labelHeight}
      z-index: ${label.zIndex || 1};
    `;

    const labelStyle = `
      font-size: ${label.fontSize}pt;
      font-family: ${label.fontFamily};
      color: ${label.color};
      text-align: ${isSlicedTable ? 'left' : label.alignment};
      font-weight: ${label.fontWeight === 'bold' || label.fontWeight === 'bold-italic' ? 'bold' : 'normal'};
      font-style: ${label.fontWeight === 'italic' || label.fontWeight === 'bold-italic' ? 'italic' : 'normal'};
      ${isSlicedTable || isSlicedList ? 'overflow: visible;' : ''}
    `;

    // For lists and tables, don't escape HTML since it's already formatted
    const shouldEscapeHtml =
      fieldType !== 'multiple-choose' && !isTableHtml && !isListHtml;
    const finalText = shouldEscapeHtml ? this.escapeHtml(text) : text;

    return `<div class="element-container" style="${containerStyle.trim()}">
      <div class="element-label" style="${labelStyle.trim()}">${finalText}</div>
    </div>`;
  }

  private renderMultipleChooseList(
    items: string[],
    tagName: string,
    showPlaceholder: boolean
  ): string {
    if (!items.length) {
      return this.resolveEmptyTagText(tagName, showPlaceholder, 'List');
    }

    let html =
      '<ul style="margin: 0; padding-left: 20px; list-style-type: disc; line-height: 1.2;">';
    items.forEach((item) => {
      html += `<li>${this.escapeHtml(item)}</li>`;
    });
    html += '</ul>';

    return html;
  }

  private renderFormTableStructure(
    field: FormField | undefined,
    tagName: string,
    showPlaceholder: boolean,
    fontSize: number = 12
  ): string {
    if (!field) {
      return this.resolveEmptyTagText(
        tagName,
        showPlaceholder,
        'Field Not Found'
      );
    }
    if (field.table) {
      return this.generateTableStructureFromForm(field.table, fontSize);
    }
    if (field.metadata?.['tableDefinition']) {
      return this.generateTableStructureFromForm(
        field.metadata['tableDefinition'],
        fontSize
      );
    }
    return this.resolveEmptyTagText(tagName, showPlaceholder, 'No Structure');
  }

  private generateImageHtml(
    image: PdfTemplateImage,
    tagData: { [tag: string]: any; },
    assets: Record<string, string | any>,
    showPlaceholder = false
  ): string {
    let imageSrc = '';
    let placeholderText = '';

    if (image.bindingTagUuid && image.bindingTagValue) {
      const tagDataItem = tagData[image.bindingTagValue.toUpperCase()];

      if (Array.isArray(tagDataItem)) {
        imageSrc = tagDataItem[0]?.url || '';
      } else if (tagDataItem) {
        if (typeof tagDataItem === 'string') {
          imageSrc = tagDataItem;
        } else {
          imageSrc =
            tagDataItem.url || tagDataItem.imageUrl || tagDataItem.value || '';
        }
      }

      if (!imageSrc && tagDataItem?.value) {
        placeholderText = tagDataItem.value;
      }
    } else if (image.assetKey && assets[image.assetKey]) {
      imageSrc = assets[image.assetKey];
    } else if ((image as any).imagePreview) {
      // Legacy / unsaved: base64 still on the element
      imageSrc = (image as any).imagePreview;
    } else if ((image as any).imageUrl) {
      imageSrc = (image as any).imageUrl;
    } else if (image.assetKey) {
      placeholderText = `[${image.assetKey}]`;
    }

    const containerStyle = `
      position: absolute;
      left: ${image.x}mm;
      top: ${image.y}mm;
      ${image.width ? `width: ${image.width}mm;` : 'width: 50mm;'}
      ${image.height ? `height: ${image.height}mm;` : 'height: 50mm;'}
      z-index: ${image.zIndex || 1};
    `;

    if (imageSrc) {
      return `<div class="element-container" style="${containerStyle.trim()}">
        <div class="element-image">
          <img src="${imageSrc}" crossorigin="anonymous" class="w-full h-full object-contain" alt="PDF Template Image" />
        </div>
      </div>`;
    }

    if (!showPlaceholder) {
      return `<div class="element-container" style="${containerStyle.trim()}">
        <div class="element-image"></div>
      </div>`;
    }

    return `<div class="element-container" style="${containerStyle.trim()}">
      <div class="element-image">${placeholderText || '[Image]'}</div>
    </div>`;
  }

  private generateTableValueHtml(
    tableValue: PdfTemplateTableValue,
    tagData: { [tag: string]: any; },
    form: Form
  ): string {
    const tableData = this.getTableData(
      tableValue.bindingTagValue || tableValue.bindingTagUuid,
      tagData,
      form
    );

    if (!tableData.definition || !tableData.data) {
      return tableValue.emptyPlaceholder
        ? `<div style="position: absolute; left: ${tableValue.x}mm; top: ${tableValue.y}mm;">${this.escapeHtml(tableValue.emptyPlaceholder)}</div>`
        : '';
    }

    const style = `
      position: absolute;
      left: ${tableValue.x}mm;
      top: ${tableValue.y}mm;
      ${tableValue.width ? `width: ${tableValue.width}mm;` : ''}
      z-index: ${tableValue.zIndex || 1};
    `;

    return `<div style="${style.trim()}">
      <app-pdf-page-table-viewer
        [tagName]="'${tableValue.bindingTagValue || tableValue.bindingTagUuid}'"
        [structure]="${JSON.stringify(tableData)}"
        [tableFontSize]="'${tableValue.tableFontSize || 'text-sm'}'"
        [cellPadding]="'${tableValue.cellPadding || 'p-2'}'"
        [maxWidth]="'${tableValue.maxWidth || '100%'}'"
        [emptyPlaceholder]="'${tableValue.emptyPlaceholder || ''}'"
      ></app-pdf-page-table-viewer>
    </div>`;
  }

  private generateLineHtml(line: PdfTemplateLine): string {
    const containerStyle = `
      position: absolute;
      left: ${line.x}mm;
      top: ${line.y}mm;
      width: ${line.width || 50}mm;
      height: ${line.height || 1}mm;
      transform: rotate(${line.rotation || 0}deg);
      transform-origin: top left;
      z-index: ${line.zIndex || 1};
    `;

    const lineStyle = `
      width: 100%;
      height: 100%;
      background-color: ${line.color || '#000000'};
    `;

    return `<div class="element-container-line" style="${containerStyle.trim()}">
      <div class="element-line" style="${lineStyle.trim()}"></div>
    </div>`;
  }

  private generateShapeHtml(shape: PdfTemplateShape): string {
    const containerStyle = `
      position: absolute;
      left: ${shape.x}mm;
      top: ${shape.y}mm;
      width: ${shape.width}mm;
      height: ${shape.height}mm;
      z-index: ${shape.zIndex || 0};
    `;

    const shapeStyle = `
      width: 100%;
      height: 100%;
      background-color: ${shape.backgroundColor};
      ${shape.borderWidth ? `border: ${shape.borderWidth}px solid ${shape.borderColor || '#000000'};` : ''}
      ${shape.borderRadius ? `border-radius: ${shape.borderRadius}px;` : ''}
    `;

    return `<div class="element-container" style="${containerStyle.trim()}">
      <div class="element-shape" style="${shapeStyle.trim()}"></div>
    </div>`;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  async generatePdf(
    template: PdfTemplate,
    formData: any,
    form: Form,
    assets: Record<string, string | any> = {}
  ): Promise<void> {
    const tagData = await this.buildTagData(formData);

    const html = await this.generateHtml(template, tagData, form, assets);

    const container = document.createElement('div');
    container.innerHTML = html;
    container.className = 'pdf-content preview-container';

    await this.pdfService.generatePDF(container, [], false);
  }

  async generateHtmlFromDictionary(
    template: PdfTemplate,
    dictionary: { [tag: string]: any; },
    form: Form,
    assets: Record<string, string | any> = {}
  ): Promise<string> {
    const tagData = this.convertSimpleDictionaryToTagData(dictionary);

    return await this.generatePreviewHtml(
      template,
      tagData,
      form,
      assets,
      false,
      ''
    );
  }

  async generatePdfFromDictionary(
    template: PdfTemplate,
    dictionary: { [tag: string]: any; },
    form: Form,
    assets: Record<string, string | any> = {},
    isDraft: boolean = true,
    watermarkText: string = 'DRAFT'
  ): Promise<void> {
    const tagData = this.convertSimpleDictionaryToTagData(dictionary);

    const html = await this.generatePreviewHtml(
      template,
      tagData,
      form,
      assets,
      isDraft,
      watermarkText
    );

    // Create a temporary container attached to body for proper rendering
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'fixed';
    tempContainer.style.left = '0';
    tempContainer.style.top = '0';
    tempContainer.style.width = '100%';
    tempContainer.style.height = '100%';
    tempContainer.style.overflow = 'auto';
    tempContainer.style.zIndex = '-1000';
    tempContainer.style.opacity = '0';
    tempContainer.style.pointerEvents = 'none';

    try {
      tempContainer.innerHTML = html;
      document.body.appendChild(tempContainer);

      // Wait for content to render
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Wait for images to load
      await this.waitForImagesToLoad(tempContainer);

      // Additional wait to ensure everything is rendered
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Generate PDF with the specified orientation
      await this.pdfService.generatePDFWithOrientation(
        tempContainer,
        [],
        false,
        template.orientation
      );
    } finally {
      // Clean up the temporary container
      if (tempContainer.parentNode) {
        document.body.removeChild(tempContainer);
      }
    }
  }

  /**
   * Wait for all images in a container to load
   */
  private async waitForImagesToLoad(container: HTMLElement): Promise<void> {
    const images = container.querySelectorAll('img');
    if (images.length === 0) {
      return;
    }

    const imagePromises = Array.from(images).map((img) => {
      return new Promise<void>((resolve) => {
        if (img.complete) {
          resolve();
        } else {
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Resolve even on error to not block the process
        }
      });
    });

    await Promise.all(imagePromises);
  }

  /**
   * Generate HTML table from table field data
   */
  private generateTableFromData(
    tableData: any,
    fontSize: number = 12,
    label?: PdfTemplateLabel,
    formField?: FormField
  ): string {
    if (!tableData || typeof tableData !== 'object') {
      return '[Table]';
    }

    const extracted = extractBoundTableData(tableData, formField);
    const table = label ? applyBoundTableSlice(extracted, label) : extracted;
    const fields = table.fields;
    const rows = table.rows;

    if (!fields.length || !rows.length) {
      return '[Empty Table]';
    }

    const hasRowTotals = table.hasRowTotals;
    const hasColumnTotals = table.hasColumnTotals;
    const headerFont = `calc(${fontSize}pt + 1px)`;
    const bodyFont = `${fontSize}pt`;
    const caption = table.continuationLabel
      ? `<div style="font-size: 8pt; color: #6b7280; font-weight: 600; margin-top: 1mm; align-self: flex-end; text-align: right; line-height: 1.2;">${this.escapeHtml(table.continuationLabel)}</div>`
      : '';
    const cellTextStyle = `padding: 2px 4px; font-size: ${bodyFont}; vertical-align: top; text-align: justify; text-justify: inter-word; overflow-wrap: anywhere; word-break: break-word; white-space: pre-wrap; line-height: 1.2;`;
    const columnPercents = table.columnWidthPercents?.length
      ? table.columnWidthPercents
      : [];
    const colgroup = columnPercents.length
      ? `<colgroup>${columnPercents
        .map((width) => `<col style="width: ${width}%;">`)
        .join('')}</colgroup>`
      : '';

    let tableHtml = `<table style="width: 100%; table-layout: fixed; border-collapse: collapse; font-size: ${bodyFont}; line-height: 1.2;">${colgroup}`;

    // Header
    tableHtml += '<thead><tr>';
    fields.forEach((field: any) => {
      tableHtml += `<th style="border: 1px solid #ccc; padding: 2px 4px; background-color: #f3f4f6; text-align: left; vertical-align: top; font-size: ${headerFont};">${this.escapeHtml(field.name || '')}</th>`;
    });
    if (hasRowTotals) {
      tableHtml += `<th style="border: 1px solid #ccc; padding: 2px 4px; background-color: #f3f4f6; text-align: center; vertical-align: top; font-size: ${headerFont};">Total</th>`;
    }
    tableHtml += '</tr></thead>';

    // Body
    tableHtml += '<tbody>';
    rows.forEach((row: any) => {
      tableHtml += '<tr>';
      fields.forEach((field: any) => {
        const cellValue = row[field.uuid] || '';
        tableHtml += `<td style="border: 1px solid #ccc; ${cellTextStyle}">${this.escapeHtml(String(cellValue))}</td>`;
      });
      if (hasRowTotals) {
        tableHtml += `<td style="border: 1px solid #ccc; padding: 2px 4px; background-color: #f9fafb; text-align: center; font-size: ${bodyFont}; vertical-align: top; line-height: 1.2;">0</td>`;
      }
      tableHtml += '</tr>';
    });
    tableHtml += '</tbody>';

    // Footer (totals row)
    if (hasColumnTotals) {
      tableHtml += '<tfoot><tr>';
      fields.forEach((field: any, index: number) => {
        if (index === 0) {
          tableHtml += `<td style="border: 1px solid #ccc; padding: 2px 4px; background-color: #e5e7eb; font-weight: bold; font-size: ${bodyFont};">Total</td>`;
        } else if (field.enable_totals) {
          tableHtml += `<td style="border: 1px solid #ccc; padding: 2px 4px; background-color: #e5e7eb; font-weight: bold; font-size: ${bodyFont};">0</td>`;
        } else {
          tableHtml += `<td style="border: 1px solid #ccc; padding: 2px 4px; background-color: #e5e7eb; font-size: ${bodyFont};"></td>`;
        }
      });
      if (hasRowTotals) {
        tableHtml += `<td style="border: 1px solid #ccc; padding: 2px 4px; background-color: #e5e7eb; text-align: center; font-weight: bold; font-size: ${bodyFont};">0</td>`;
      }
      tableHtml += '</tr></tfoot>';
    }

    tableHtml += '</table>';

    return `<div style="display: flex; flex-direction: column;">${tableHtml}${caption}</div>`;
  }

  /**
   * Handle file upload field display
   */
  private handleFileUpload(data: any): string {
    const imageExtensions = [
      '.jpg',
      '.jpeg',
      '.png',
      '.gif',
      '.webp',
      '.bmp',
      '.svg',
    ];

    // Check if it's an image or file
    let url = '';
    if (typeof data === 'string') {
      url = data;
    } else if (Array.isArray(data) && data.length > 0) {
      url = data[0]?.url || data[0]?.imageUrl || '';
    } else if (data && typeof data === 'object') {
      url = data.url || data.imageUrl || '';
    }

    if (!url) {
      return '[File]';
    }

    // Check if it's an image
    const ext = url.toLowerCase().substring(url.lastIndexOf('.'));
    if (imageExtensions.includes(ext)) {
      return '[Image]';
    } else {
      // Return filename
      const filename = url.split('/').pop() || 'file';
      return this.escapeHtml(filename);
    }
  }

  private generateTableStructureFromForm(
    table: any,
    fontSize: number = 12
  ): string {
    const allFields = table.fields || table.definition?.columns || [];

    if (!allFields.length) {
      return '[Empty Table]';
    }

    const columnFields = allFields.filter(
      (f: any) => f.type === 'column' || (!f.type && !f.sub_fields?.length)
    );
    const fields = columnFields.length > 0 ? columnFields : allFields;

    const normalizedFields = fields.map((field: any) => ({
      ...field,
      uuid: String(field.uuid || '')
        .replace(/[-_]/g, '')
        .toUpperCase(),
    }));

    const hasRowTotals =
      normalizedFields.some((f: any) => f.enable_row_totals) || false;
    const hasColumnTotals =
      normalizedFields.some((f: any) => f.enable_totals) || false;

    let rows = (table.rows || []).map((row: any) => {
      const normalizedRow: any = {};
      Object.keys(row || {}).forEach((key) => {
        const normalizedKey = key.replace(/[-_]/g, '').toUpperCase();
        normalizedRow[normalizedKey] = row[key];
      });
      return normalizedRow;
    });

    if (rows.length === 0) {
      rows = Array.from({ length: 3 }, () => ({}));
    }

    const headerFont = `calc(${fontSize}pt + 1px)`;
    const bodyFont = `${fontSize}pt`;

    let tableHtml = `<table style="width: 100%; border-collapse: collapse;">`;

    // Header
    tableHtml += '<thead><tr>';
    normalizedFields.forEach((field: any) => {
      tableHtml += `<th style="border: 1px solid #ccc; padding: 4px 8px; background-color: #f3f4f6; text-align: left; font-size: ${headerFont};">${this.escapeHtml(field.name || '')}</th>`;
    });
    if (hasRowTotals) {
      tableHtml += `<th style="border: 1px solid #ccc; padding: 4px 8px; background-color: #f3f4f6; text-align: center; font-size: ${headerFont};">Total</th>`;
    }
    tableHtml += '</tr></thead>';

    // Body: definition rows (or 3 placeholders); keep predefined values
    tableHtml += '<tbody>';
    rows.forEach((row: any) => {
      tableHtml += '<tr>';
      normalizedFields.forEach((field: any) => {
        const raw = row[field.uuid];
        const cellValue =
          raw === null || raw === undefined || raw === ''
            ? 'Sample'
            : String(raw);
        tableHtml += `<td style="border: 1px solid #ccc; padding: 4px 8px; font-size: ${bodyFont};">${this.escapeHtml(cellValue)}</td>`;
      });
      if (hasRowTotals) {
        tableHtml += `<td style="border: 1px solid #ccc; padding: 4px 8px; background-color: #f9fafb; text-align: center; font-size: ${bodyFont};">0</td>`;
      }
      tableHtml += '</tr>';
    });
    tableHtml += '</tbody>';

    // Footer (totals row)
    if (hasColumnTotals) {
      tableHtml += '<tfoot><tr>';
      normalizedFields.forEach((field: any, index: number) => {
        if (index === 0) {
          tableHtml += `<td style="border: 1px solid #ccc; padding: 4px 8px; background-color: #e5e7eb; font-weight: bold; font-size: ${bodyFont};">Total</td>`;
        } else if (field.enable_totals) {
          tableHtml += `<td style="border: 1px solid #ccc; padding: 4px 8px; background-color: #e5e7eb; font-weight: bold; font-size: ${bodyFont};">0</td>`;
        } else {
          tableHtml += `<td style="border: 1px solid #ccc; padding: 4px 8px; background-color: #e5e7eb; font-size: ${bodyFont};"></td>`;
        }
      });
      if (hasRowTotals) {
        tableHtml += `<td style="border: 1px solid #ccc; padding: 4px 8px; background-color: #e5e7eb; text-align: center; font-weight: bold; font-size: ${bodyFont};">0</td>`;
      }
      tableHtml += '</tr></tfoot>';
    }

    tableHtml += '</table>';
    return tableHtml;
  }
}
